import { GameState } from '../../engine/types';
import { evaluate } from '../../engine/evaluation';
import './EvaluationBar.css';

interface EvaluationBarProps {
  gameState: GameState;
}

/**
 * EvaluationBar component - displays position evaluation
 * Shows who's winning and by how much using a visual bar
 */
export function EvaluationBar({ gameState }: EvaluationBarProps) {
  // Get evaluation score (positive = white advantage, negative = black advantage)
  const score = evaluate(gameState);
  
  // Convert to pawns (score is in centipawns, divide by 100)
  const pawns = score / 100;
  
  // Cap display at ±10 pawns for better visualization
  const cappedScore = Math.max(-10, Math.min(10, pawns));
  
  // Calculate percentage for bar (0 to 100)
  // -10 pawns = 0%, 0 pawns = 50%, +10 pawns = 100%
  const percentage = ((cappedScore + 10) / 20) * 100;
  
  // Format score for display
  const formatScore = (score: number): string => {
    const absPawns = Math.abs(score);
    if (absPawns >= 1) {
      return score > 0 ? `+${absPawns.toFixed(1)}` : `${absPawns.toFixed(1)}`;
    } else {
      return score > 0 ? `+${(absPawns * 100).toFixed(0)}¢` : `${(absPawns * 100).toFixed(0)}¢`;
    }
  };
  
  return (
    <div className="evaluation-bar-container">
      <div className="evaluation-header">
        <h3>Position Evaluation</h3>
      </div>
      
      <div className="evaluation-bar-wrapper">
        <div className="evaluation-bar">
          <div 
            className="evaluation-bar-white" 
            style={{ height: `${percentage}%` }}
          >
            {percentage > 60 && (
              <span className="evaluation-text white-text">
                {formatScore(pawns)}
              </span>
            )}
          </div>
          <div className="evaluation-center-line"></div>
          {percentage <= 60 && percentage >= 40 && (
            <div className="evaluation-text-center">
              {pawns === 0 ? '=' : formatScore(pawns)}
            </div>
          )}
          {percentage < 40 && (
            <div className="evaluation-text black-text">
              {formatScore(pawns)}
            </div>
          )}
        </div>
        <div className="evaluation-labels">
          <div className="evaluation-label white-label">White</div>
          <div className="evaluation-label black-label">Black</div>
        </div>
      </div>
      
      <div className="evaluation-description">
        {pawns > 3 ? (
          <span className="eval-advantage white-advantage">White is winning</span>
        ) : pawns > 1 ? (
          <span className="eval-advantage white-slight">White has advantage</span>
        ) : pawns > -1 ? (
          <span className="eval-advantage equal">Position is equal</span>
        ) : pawns > -3 ? (
          <span className="eval-advantage black-slight">Black has advantage</span>
        ) : (
          <span className="eval-advantage black-advantage">Black is winning</span>
        )}
      </div>
    </div>
  );
}
